import { Check, CloseRound } from "@components/icons"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import colors from "@constants/colors"
import { resetButtonStyles } from "@constants/styles"
import { smallcapsBold14Styles } from "@constants/typography"
import React from "react"
import ReactDOM from "react-dom"
import styled, { css } from "styled-components"

import DesktopPDPMuxPlayer from "./DesktopMuxPlayer"
import { MuxVideo as MuxVideoType } from "./index"
import ZoomedImage from "./ZoomedImage"

const Container = styled.div`
  z-index: 10000;
  position: fixed;
  top: 0;
  left: 0;
  overflow: auto;
  width: 100vw;
  height: 100vh;
  background-color: ${colors.white};
`

const Sidebar = styled.div`
  ${smallcapsBold14Styles}

  z-index: 10000;
  position: fixed;
  top: 0;
  left: 0;
  flex-direction: column;
  display: flex;
  height: 100vh;
  padding-top: 40px;
  overflow-y: auto;
`

const Images = styled.div<{ $scale: 1 | 2 }>`
  width: ${({ $scale }) => ($scale === 1 ? "50vw" : "100vw")};
  margin: 0 auto;
`

const Thumbnail = styled.div`
  height: 72px;
  width: 72px;
  position: relative;
  margin-bottom: 20px;
  margin-left: 3px;
  cursor: pointer;

  padding-left: 3px;
`

const ThumbnailWrapper = styled.div`
  ${ImageHelperContainer} {
    width: 72px;
    height: 72px;

    img {
      object-fit: cover;
    }
  }
`

const ThumbnailOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 3;

  display: flex;
  justify-content: center;
  align-items: center;

  height: 100%;
  width: 100%;
  z-index: 10000000;
  background-color: rgba(0, 0, 0, 0.65);
`

const ZoomButton = styled.button<{ $active: boolean }>`
  ${({ $active }) => css`
    ${resetButtonStyles}

    padding: 24px;
    border-left: 3px solid ${$active ? colors.black : colors.white};
    color: ${$active ? colors.black : colors.mediumDarkGray};

    &:nth-child(2) {
      margin-bottom: 25px;
    }
  `}
`

const Close = styled.button`
  ${resetButtonStyles}

  position: fixed;
  top: 20px;
  right: 30px;
  z-index: 10000000;
  width: 64px;
  height: 64px;
`

const MuxWrapper = styled.div<{ $scale: 1 | 2 }>`
  max-width: ${({ $scale }) => ($scale === 1 ? "100%" : "90%")};
  margin: 10vh auto;
`

const MuxThumbnailWrapper = styled.div`
  pointer-events: none;
`

interface ZoomedImageGallery {
  media: Array<MuxVideoType | string>
  mediaScrollToItem: MuxVideoType | string
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void
}

const ZoomedImageGallery: React.FC<ZoomedImageGallery> = ({
  media,
  mediaScrollToItem,
  toggleIsZoomedImageGalleryOpen,
}) => {
  const mediaScrollToIndex = media.indexOf(mediaScrollToItem)
  const defaultActiveMedia = mediaScrollToIndex === -1 ? 0 : mediaScrollToIndex
  const rootEl = document.getElementById("___gatsby")
  const [scale, setScale] = React.useState<1 | 2>(2)
  const [activeImage, setActiveImage] = React.useState(defaultActiveMedia)
  let scrollRef = React.createRef<HTMLDivElement>()

  React.useEffect(() => {
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = "auto"
    }
  }, [])

  React.useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    })
  }, [])

  const mappedImageComponents: Array<{
    fullImage: JSX.Element
    thumbnail: JSX.Element
  }> = media?.reduce((acc, val, index) => {
    const fullImageRef = React.createRef<HTMLDivElement>()

    if (index === activeImage) {
      scrollRef = fullImageRef
    }

    const fullImage =
      typeof val === "string" ? (
        <ZoomedImage
          image={val}
          key={index + "fullImg"}
          ref={fullImageRef}
          scale={scale}
          toggleActive={() => setActiveImage(index)}
        />
      ) : (
        <MuxWrapper $scale={scale} ref={fullImageRef}>
          <DesktopPDPMuxPlayer fullWidth muxVideo={val} />
        </MuxWrapper>
      )

    const thumbnail = (
      <Thumbnail
        // @ts-expect-error Ignore
        {...val}
        key={index + "thumbnail"}
        onClick={() => {
          fullImageRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          })
        }}
        role="button"
      >
        {activeImage === index && (
          <ThumbnailOverlay>
            <Check />
          </ThumbnailOverlay>
        )}
        <ThumbnailWrapper>
          {/* If typeof not string, we can assume its the mux video object */}
          {typeof val === "string" ? (
            <ImageHelper alt="Product image thumbnail" src={val as string} />
          ) : (
            <MuxThumbnailWrapper>
              <DesktopPDPMuxPlayer muxVideo={val} />
            </MuxThumbnailWrapper>
          )}
        </ThumbnailWrapper>
      </Thumbnail>
    )

    return [...acc, { fullImage, thumbnail }]
  }, [])

  return (
    rootEl &&
    ReactDOM.createPortal(
      <Container role="dialog">
        <Close onClick={() => toggleIsZoomedImageGalleryOpen(false)}>
          <CloseRound />
        </Close>
        <Sidebar>
          <ZoomButton
            $active={scale === 1}
            onClick={() => {
              setScale(1)
            }}
          >
            1x
          </ZoomButton>
          <ZoomButton
            $active={scale === 2}
            onClick={() => {
              setScale(2)
            }}
          >
            2x
          </ZoomButton>
          {mappedImageComponents.map((img) => img.thumbnail)}
        </Sidebar>
        <Images $scale={scale}>
          {mappedImageComponents.map((img) => img.fullImage)}
        </Images>
      </Container>,
      document.getElementById("___gatsby")!
    )
  )
}

export default ZoomedImageGallery
